import type { Tenant } from './types';
import { PAISES, esPaisConocido, monedaDe, nombrePais } from './countries';
import { esPaisUE } from './tax';

/**
 * Multi-mercado: la moneda
 * vive en el Tenant (ISO 4217) y TODO importe visible se formatea con ella.
 *
 * El selector cubre todas las monedas asociadas a los paises disponibles.
 * Los decimales salen de Intl/ISO 4217, con las dos excepciones de producto
 * que ya usabamos: pesos chilenos y colombianos se muestran sin decimales.
 */

export type CountryCode = Tenant['country'];

export interface CurrencyInfo {
  code: string;
  /** Nombre para el selector de Ajustes. */
  label: string;
  /** Locale con el que se formatea (agrupacion y simbolo correctos). */
  locale: string;
  /** Decimales con los que se muestra el dinero del dia a dia. */
  decimals: number;
}

const CURRENCY_OVERRIDES: Record<string, CurrencyInfo> = {
  EUR: { code: 'EUR', label: 'Euro (€)', locale: 'es-ES', decimals: 2 },
  USD: { code: 'USD', label: 'Dólar estadounidense ($)', locale: 'es-US', decimals: 2 },
  MXN: { code: 'MXN', label: 'Peso mexicano ($)', locale: 'es-MX', decimals: 2 },
  COP: { code: 'COP', label: 'Peso colombiano ($)', locale: 'es-CO', decimals: 0 },
  ARS: { code: 'ARS', label: 'Peso argentino ($)', locale: 'es-AR', decimals: 2 },
  CLP: { code: 'CLP', label: 'Peso chileno ($)', locale: 'es-CL', decimals: 0 },
  PEN: { code: 'PEN', label: 'Sol peruano (S/)', locale: 'es-PE', decimals: 2 },
};

const PRIORITY_CURRENCIES = Object.keys(CURRENCY_OVERRIDES);
const COUNTRY_CURRENCIES = [...new Set(PAISES.map((pais) => pais.currency))];

let currencyNames: Intl.DisplayNames | undefined;

function capitalized(value: string): string {
  return value.charAt(0).toLocaleUpperCase('es') + value.slice(1);
}

function generatedCurrencyInfo(code: string): CurrencyInfo {
  let name = code;
  let symbol = code;
  let decimals = 2;
  try {
    currencyNames ??= new Intl.DisplayNames(['es'], { type: 'currency' });
    name = currencyNames.of(code) ?? code;
    const formatter = new Intl.NumberFormat('es', {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    });
    symbol = formatter.formatToParts(0).find((part) => part.type === 'currency')?.value ?? code;
    decimals = formatter.resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    // Un runtime con ICU reducido sigue ofreciendo el codigo ISO y 2 decimales.
  }
  const qualifier = symbol === code ? code : `${symbol} · ${code}`;
  return { code, label: `${capitalized(name)} (${qualifier})`, locale: 'es', decimals };
}

const remainingCurrencies = COUNTRY_CURRENCIES
  .filter((code) => !CURRENCY_OVERRIDES[code])
  .map(generatedCurrencyInfo)
  .sort((a, b) => a.label.localeCompare(b.label, 'es'));

/**
 * Monedas operativas de todos los paises disponibles. Las habituales van
 * primero y el resto en orden alfabetico por su nombre en espanyol.
 */
export const CURRENCIES: Record<string, CurrencyInfo> = Object.fromEntries([
  ...PRIORITY_CURRENCIES.map((code) => [code, CURRENCY_OVERRIDES[code]] as const),
  ...remainingCurrencies.map((info) => [info.code, info] as const),
]);

/**
 * Info de una moneda, tambien si no esta en la lista (un tenant antiguo o
 * un codigo guardado a mano): se formatea igual con su codigo ISO.
 */
export function currencyInfo(code: string | undefined): CurrencyInfo {
  const normalized = (code ?? 'EUR').trim().toUpperCase();
  return (
    CURRENCIES[normalized] ?? generatedCurrencyInfo(normalized || 'EUR')
  );
}

export interface CountryPreset {
  label: string;
  currency: string;
  /** IVA (o equivalente) tipico de restauracion, como sugerencia. */
  vatPct: number;
  /** Como se llama el impuesto sobre la venta en ese pais. */
  taxName: string;
  /** Matiz que merece la pena ensenyar junto a la sugerencia. */
  note?: string;
  /**
   * true cuando el pais no tiene preset propio y la sugerencia es generica:
   * la pantalla lo dice («ajusta el IVA de tu pais») en vez de afirmar un
   * tipo que no conocemos.
   */
  generico?: boolean;
}

/**
 * Presets por pais. Son SUGERENCIAS de alta: el usuario siempre puede
 * cambiar moneda e IVA por su cuenta (regimenes especiales, fronteras...).
 *
 * Solo los mercados con clientes tienen preset propio; para cualquier otro
 * pais de la tabla (`countries.ts`) `countryPreset` fabrica uno generico con
 * su moneda. Usar SIEMPRE `countryPreset(code)`, nunca indexar este objeto:
 * el pais del tenant puede ser cualquiera de los 240.
 */
/**
 * Tipos de IVA (o equivalente) que suelen llevar las COMPRAS de un
 * restaurante, para elegir con un toque al escribir un precio «con IVA».
 * El primero es el más común. Sin lista propia, el tipo del país.
 */
const IVA_COMPRAS: Record<string, number[]> = {
  MX: [16, 0],
  ES: [10, 21, 4],
  CO: [19, 5, 0],
  AR: [21, 10.5],
  CL: [19],
  PE: [18],
};
export function purchaseVatOptions(country: string | undefined): number[] {
  const code = (country ?? '').toUpperCase();
  return IVA_COMPRAS[code] ?? [countryPreset(code).vatPct];
}

export const COUNTRY_PRESETS: Record<string, CountryPreset> = {
  ES: {
    label: 'España',
    currency: 'EUR',
    vatPct: 10,
    taxName: 'IVA',
    note: 'El 10 % es el tipo de hostelería; bebidas alcohólicas en tienda van al 21 %.',
  },
  MX: { label: 'México', currency: 'MXN', vatPct: 16, taxName: 'IVA' },
  CO: {
    label: 'Colombia',
    currency: 'COP',
    vatPct: 8,
    taxName: 'INC',
    note: 'Los restaurantes suelen tributar impoconsumo (INC) del 8 %, no IVA.',
  },
  AR: { label: 'Argentina', currency: 'ARS', vatPct: 21, taxName: 'IVA' },
  CL: { label: 'Chile', currency: 'CLP', vatPct: 19, taxName: 'IVA' },
  PE: { label: 'Perú', currency: 'PEN', vatPct: 18, taxName: 'IGV' },
  US: {
    label: 'Estados Unidos',
    currency: 'USD',
    vatPct: 8,
    taxName: 'Sales tax',
    note: 'El sales tax lo fija cada estado y cada ciudad: va del 0 % de Oregón al 10 % largo de algunas ciudades. El 8 % es solo un punto de partida.',
  },
};

/** Paises con preset propio, en el orden en que se ofrecen como habituales. */
export const COUNTRY_CODES = Object.keys(COUNTRY_PRESETS) as CountryCode[];

/**
 * Preset del pais, sea cual sea. Para los que no tienen uno propio, uno
 * generico: nombre real, su moneda, y un 10 % de partida marcado como
 * `generico` para que la pantalla pida revisarlo. Los tenants antiguos con
 * «OTHER» (o cualquier codigo desconocido) caen aqui con USD.
 */
export function countryPreset(code: string | undefined): CountryPreset {
  const c = (code ?? '').trim().toUpperCase();
  const propio = COUNTRY_PRESETS[c];
  if (propio) return propio;
  const conocido = esPaisConocido(c);
  return {
    label: conocido ? (nombrePais(c) ?? c) : 'Otro país',
    currency: conocido ? monedaDe(c) : 'USD',
    vatPct: 10,
    taxName: conocido && esPaisUE(c) ? 'IVA' : 'Impuesto sobre la venta',
    generico: true,
  };
}

/**
 * Moneda en la que se le COBRA al restaurante, que no es la misma que la
 * moneda con la que lleva sus numeros.
 *
 * Decision de Alex (27 ago 2026): euros dentro de Europa, dolares fuera, con
 * la misma cifra en las dos (49 EUR = 49 USD). No es una conversion — es un
 * precio por mercado, asi que no se toca cuando se mueve el tipo de cambio.
 *
 * Un restaurante mexicano lleva su contabilidad en MXN (`tenant.currency`) y
 * paga la suscripcion en USD (esto). Son dos cosas distintas a proposito.
 */
const EUROPA_FUERA_DE_LA_UE: readonly string[] = ['GB', 'CH', 'NO', 'IS', 'LI', 'AD', 'MC', 'SM', 'VA'];

export function billingCurrency(country: CountryCode): 'EUR' | 'USD' {
  const c = (country ?? '').trim().toUpperCase();
  return esPaisUE(c) || EUROPA_FUERA_DE_LA_UE.includes(c) ? 'EUR' : 'USD';
}

/**
 * Formatea un importe en la moneda dada, con el locale y los decimales de
 * esa moneda. Es la version del MOTOR (textos de alertas, asistente...);
 * la web tiene la suya con cache de formateadores en `format.ts`.
 */
export function formatMoneyIn(value: number, currency: string | undefined): string {
  const info = currencyInfo(currency);
  const safe = Number.isFinite(value) ? value : 0;
  try {
    return new Intl.NumberFormat(info.locale, {
      style: 'currency',
      currency: info.code,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: info.decimals,
      maximumFractionDigits: info.decimals,
      // Miles siempre, también con cuatro cifras: en es-ES Intl escribe
      // «2237,81 €» por defecto (revisión de Alex, 14 oct 2026: «2.237,81 €»).
      useGrouping: true,
    }).format(safe === 0 ? 0 : safe);
  } catch {
    // Codigo ISO invalido guardado a mano: mejor "123,45 XXX" que petar.
    return `${safe.toFixed(2)} ${info.code}`;
  }
}
